'use client';

import React from 'react';
import { cn } from '@sharpr/ui';
import type { Sport, LineupInfo } from '@sharpr/types';
import { TeamLogo } from './TeamLogo';
import { NotesIcon, isProjectedLineup } from './NotesContent';

interface LineupTriggerProps {
  lineup?: LineupInfo;
  sport: Sport;
  /** NBA: the analyst wrote a note on the player himself; the trigger shows gold. */
  playerNote?: boolean;
  className?: string;
}

export function LineupTrigger({ lineup, sport, playerNote, className }: LineupTriggerProps) {
  const projected = isProjectedLineup(lineup);

  return (
    <span
      className={cn(
        'inline-flex items-center transition-colors duration-150',
        projected || playerNote ? 'text-amber-500' : 'text-muted-foreground',
        playerNote ? 'hover:text-amber-600 dark:hover:text-amber-400' : 'hover:text-fg dark:hover:text-text-primary',
        className,
      )}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center rounded transition-colors duration-150',
          'p-2 -m-2 md:p-1 md:-m-1',
          'md:hover:bg-muted/50 md:dark:hover:bg-white/5',
        )}
      >
        <NotesIcon sport={sport} className="w-3.5 h-3.5" />
      </span>
    </span>
  );
}

export function LineupDrawerHeader({ lineup, sport }: { lineup?: LineupInfo; sport: Sport }) {
  const projected = isProjectedLineup(lineup);

  return (
    <div className="flex items-center gap-2 min-w-0">
      <NotesIcon sport={sport} className={cn('w-4 h-4 flex-shrink-0', projected ? 'text-amber-500' : 'text-muted-foreground')} />
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={cn('text-sm font-semibold truncate', projected ? 'text-amber-600 dark:text-amber-400' : 'text-fg dark:text-text-primary')}>
          {projected ? 'Projected lineup' : 'Confirmed lineup'}
        </span>
        {lineup?.team && (
          <>
            <span className="text-muted-foreground">·</span>
            <TeamLogo team={lineup.team} sport={sport} className="w-4 h-4" />
            <span className="text-xs font-medium text-muted-foreground">{lineup.team}</span>
          </>
        )}
      </div>
    </div>
  );
}
